import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';
import { apiFetchPaginated } from '../lib/api';
import { formatRelative } from '../lib/format';
import { CreateTicketModal } from './CreateTicketModal';
import { notify } from '../lib/notify';
import { useNotificationsStore } from '../lib/notifications-store';
import type { Ticket, TicketStatus, TicketPriority, TicketChannel } from '@vipsoft/shared-types';

type StatusFilter = TicketStatus | 'all' | 'active';

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'active', label: 'Activos' },
  { value: 'all', label: 'Todos' },
  { value: 'new', label: 'Nuevos' },
  { value: 'open', label: 'Abiertos' },
  { value: 'in_progress', label: 'En proceso' },
  { value: 'waiting_customer', label: 'Esperando cliente' },
  { value: 'resolved', label: 'Resueltos' },
  { value: 'closed', label: 'Cerrados' },
];

const STATUS_LABEL: Record<TicketStatus, string> = {
  new: 'Nuevo',
  open: 'Abierto',
  in_progress: 'En proceso',
  waiting_customer: 'Esperando cliente',
  resolved: 'Resuelto',
  closed: 'Cerrado',
  reopened: 'Reabierto',
};

const STATUS_STYLE: Record<TicketStatus, string> = {
  new: 'text-slate-700 bg-slate-100',
  open: 'text-blue-700 bg-blue-50',
  in_progress: 'text-indigo-700 bg-indigo-50',
  waiting_customer: 'text-amber-700 bg-amber-50',
  resolved: 'text-emerald-700 bg-emerald-50',
  closed: 'text-slate-500 bg-slate-100',
  reopened: 'text-orange-700 bg-orange-50',
};

const PRIORITY_LABEL: Record<TicketPriority, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  normal: 'Normal',
  low: 'Baja',
};

const PRIORITY_STYLE: Record<TicketPriority, string> = {
  urgent: 'text-rose-700 bg-rose-50 ring-rose-200',
  high: 'text-orange-700 bg-orange-50 ring-orange-200',
  normal: 'text-slate-700 bg-slate-50 ring-slate-200',
  low: 'text-slate-500 bg-slate-50 ring-slate-200',
};

interface TicketsResponse {
  data: Ticket[];
  meta: { total: number; page: number; per_page: number; last_page: number };
}

const PRIORITY_FILTER_OPTIONS: Array<{ value: TicketPriority | ''; label: string }> = [
  { value: '', label: 'Cualquier prioridad' },
  { value: 'urgent', label: 'Urgente' },
  { value: 'high', label: 'Alta' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Baja' },
];

const CHANNEL_FILTER_OPTIONS: Array<{ value: TicketChannel | ''; label: string }> = [
  { value: '', label: 'Cualquier canal' },
  { value: 'email', label: 'Correo' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'widget', label: 'Widget web' },
  { value: 'api', label: 'API' },
];

interface AssigneeOption {
  id: number;
  name: string;
}

export default function Tickets() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [meta, setMeta] = useState<TicketsResponse['meta']>({
    total: 0,
    page: 1,
    per_page: 20,
    last_page: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | ''>('');
  const [channelFilter, setChannelFilter] = useState<TicketChannel | ''>('');
  const [assigneeFilter, setAssigneeFilter] = useState<string>(''); // '' = todos, 'unassigned' = sin asignar, '123' = user id
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [assigneeOptions, setAssigneeOptions] = useState<AssigneeOption[]>([]);
  const navigate = useNavigate();
  const markAllRead = useNotificationsStore((s) => s.markAllRead);

  // Al entrar a la lista de tickets damos por leídas todas las
  // notificaciones acumuladas — el badge del sidebar vuelve a 0.
  useEffect(() => {
    markAllRead();
  }, [markAllRead]);

  // Cargamos la lista de agentes/supervisores/admins para el dropdown de
  // "Asignado a". Se hace una sola vez al montar — la lista cambia poco.
  useEffect(() => {
    apiFetchPaginated<{ id: number; name: string; role: string; is_active: boolean }>(
      '/users?per_page=100',
    )
      .then((res) => {
        setAssigneeOptions(
          res.data
            .filter((u) => u.is_active && (u.role === 'agent' || u.role === 'supervisor' || u.role === 'admin'))
            .map((u) => ({ id: u.id, name: u.name })),
        );
      })
      .catch(() => setAssigneeOptions([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    setError('');
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('per_page', '20');
    if (statusFilter === 'active') {
      params.set('status_group', 'active');
    } else if (statusFilter !== 'all') {
      params.set('status', statusFilter);
    }
    if (priorityFilter) params.set('priority', priorityFilter);
    if (channelFilter) params.set('channel', channelFilter);
    if (assigneeFilter === 'unassigned') {
      params.set('unassigned_only', 'true');
    } else if (assigneeFilter) {
      params.set('assignee_id', assigneeFilter);
    }
    if (search.trim()) params.set('q', search.trim());

    apiFetchPaginated<Ticket>(`/tickets?${params.toString()}`)
      .then((res) => {
        setTickets(res.data);
        setMeta(res.meta);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [statusFilter, priorityFilter, channelFilter, assigneeFilter, search, page]);

  const activeFilterCount =
    (priorityFilter ? 1 : 0) + (channelFilter ? 1 : 0) + (assigneeFilter ? 1 : 0);
  const clearAdvancedFilters = () => {
    setPriorityFilter('');
    setChannelFilter('');
    setAssigneeFilter('');
    setPage(1);
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Tickets</h1>
          <p className="text-sm text-slate-500 mt-1">
            {meta.total > 0
              ? `${meta.total} ticket${meta.total === 1 ? '' : 's'} ${
                  statusFilter === 'all'
                    ? 'en total'
                    : (STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label ?? '').toLowerCase()
                }`
              : 'No hay tickets que coincidan con el filtro seleccionado'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-slate-900 hover:bg-slate-800 text-white"
          >
            <Plus size={14} />
            Nuevo ticket
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200">
        <div className="p-4 border-b border-slate-100 flex flex-col gap-3">
          {/* Fila 1: búsqueda + filtros de status pills */}
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Buscar por código (TCK-…) o asunto…"
              className="flex-1 min-w-[200px] px-3 py-2 text-sm rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white"
            />
            <div className="flex items-center gap-1 flex-wrap">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    setStatusFilter(opt.value);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                    statusFilter === opt.value
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {/* Fila 2: filtros avanzados — prioridad / canal / asignado */}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <FilterSelect
              value={priorityFilter}
              onChange={(v) => {
                setPriorityFilter(v as TicketPriority | '');
                setPage(1);
              }}
              options={PRIORITY_FILTER_OPTIONS}
            />
            <FilterSelect
              value={channelFilter}
              onChange={(v) => {
                setChannelFilter(v as TicketChannel | '');
                setPage(1);
              }}
              options={CHANNEL_FILTER_OPTIONS}
            />
            <FilterSelect
              value={assigneeFilter}
              onChange={(v) => {
                setAssigneeFilter(v);
                setPage(1);
              }}
              options={[
                { value: '', label: 'Cualquier asignación' },
                { value: 'unassigned', label: 'Sin asignar' },
                ...assigneeOptions.map((u) => ({ value: String(u.id), label: u.name })),
              ]}
            />
            {activeFilterCount > 0 && (
              <button
                onClick={clearAdvancedFilters}
                className="inline-flex items-center gap-1 px-2 py-1 text-slate-500 hover:text-slate-900"
                title="Limpiar filtros avanzados"
              >
                <X size={12} />
                Limpiar {activeFilterCount}
              </button>
            )}
          </div>
        </div>

        {loading && (
          <div className="px-5 py-16 text-sm text-slate-500 text-center flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            Cargando tickets…
          </div>
        )}
        {error && !loading && (
          <div className="px-5 py-10 text-sm text-rose-600 text-center">{error}</div>
        )}
        {!loading && !error && tickets.length === 0 && (
          <div className="px-5 py-16 text-sm text-slate-500 text-center">
            No hay tickets que coincidan con los filtros actuales.
          </div>
        )}

        {!loading && tickets.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/70">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-medium">Ticket</th>
                  <th className="px-5 py-3 font-medium">Canal</th>
                  <th className="px-5 py-3 font-medium">Estado</th>
                  <th className="px-5 py-3 font-medium">Prioridad</th>
                  <th className="px-5 py-3 font-medium">Creado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tickets.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50 transition">
                    <td className="px-5 py-3">
                      <Link
                        to={`/admin/tickets/${t.code}`}
                        className="font-medium text-slate-900 leading-tight block hover:text-blue-600 truncate max-w-[400px]"
                      >
                        {t.subject}
                      </Link>
                      <div className="text-[11px] text-slate-500 mt-0.5">{t.code}</div>
                    </td>
                    <td className="px-5 py-3 text-slate-700 capitalize text-sm">{t.channel}</td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLE[t.status]}`}
                      >
                        {STATUS_LABEL[t.status]}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ring-1 ${PRIORITY_STYLE[t.priority]}`}
                      >
                        {PRIORITY_LABEL[t.priority]}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-slate-500 text-xs">
                      {formatRelative(t.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <CreateTicketModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={(ticket) => {
            setCreateOpen(false);
            notify.success('Ticket creado.', ticket.code);
            navigate(`/admin/tickets/${ticket.code}`);
          }}
        />

        {meta.last_page > 1 && (
          <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-sm">
            <div className="text-slate-500">
              Página {meta.page} de {meta.last_page}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
                disabled={page === meta.last_page}
                className="p-2 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

interface FilterSelectOption {
  value: string;
  label: string;
}

function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: FilterSelectOption[];
}) {
  const isActive = value !== '';
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`px-2.5 py-1.5 text-xs rounded-md border transition ${
        isActive
          ? 'bg-slate-900 text-white border-slate-900'
          : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
      }`}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value} className="bg-white text-slate-900">
          {opt.label}
        </option>
      ))}
    </select>
  );
}
